import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Save, X, Plus, Edit, Trash2, BookOpen, List, ChevronUp, ChevronDown, Wand2 } from 'lucide-react';
import { RichTextEditor, VideoInput, DocumentInput, DownloadManager, ImageManager } from '../../ContentEditors';
import LessonPlanBuilderModal, { planToMarkdown } from '../../LessonPlanBuilderModal';
import AIWorkpad from './AIWorkpad';
import QuizRegenerateButton from './QuizRegenerateButton';
import ImageRegenerateButton from './ImageRegenerateButton';
import CitationTools from './CitationTools';
import { stringifyError } from '../../../utils/errors';
import KBCitationSidebar from './KBCitationSidebar';
import CitedSourcesChipStrip from './CitedSourcesChipStrip';
import LessonAIBuilder from './LessonAIBuilder';
import LessonPreviewModal from './LessonPreviewModal';
import MarkdownAIToolbar from './MarkdownAIToolbar';

const LessonForm = ({
 lessonForm,
 setLessonForm,
 editingLesson,
 courses,
 courseDetails,
 contentSectionForm,
 setContentSectionForm,
 editingSectionId,
 quizQuestion,
 setQuizQuestion,
 editingQuestionIndex,
 expandedSections,
 setExpandedSections,
 onSave,
 onCancel,
 onAddContentSection,
 onEditContentSection,
 onCancelEditSection,
 onRemoveContentSection,
 onMoveContentSection,
 onAddQuizQuestion,
 onEditQuizQuestion,
 onUpdateQuizQuestion,
 onCancelEditQuizQuestion,
 onRemoveQuizQuestion
}) => {
 // KB regeneration axios calls need the API base — pulled from the same
 // AuthContext used elsewhere in the admin surface.
 const { API } = useContext(AuthContext);
 // Local state for the AI lesson-plan builder modal — kept inside the form
 // so it doesn't leak into the parent's massive LMSAdmin state.
 const [showPlanBuilder, setShowPlanBuilder] = useState(false);
 const [showWorkpad, setShowWorkpad] = useState(false);
 const [showPreview, setShowPreview] = useState(false);

 // Highlight-to-filter: track the admin's active text selection inside
 // the lesson-body editor so the KB sidebar can filter itself to docs
 // that match. Kept debounced via a raw string; the sidebar runs its
 // own /admin/ai-knowledge/search call with its own debounce.
 const [bodyHighlight, setBodyHighlight] = useState('');
 const editorWrapperRef = useRef(null);
 useEffect(() => {
   const onSel = () => {
     const sel = typeof window !== 'undefined' ? window.getSelection() : null;
     if (!sel || sel.isCollapsed || !sel.rangeCount) { setBodyHighlight(''); return; }
     const wrap = editorWrapperRef.current;
     if (!wrap) return;
     // Only react to selections whose anchor is inside the editor wrapper.
     const anchor = sel.anchorNode;
     if (!anchor || !wrap.contains(anchor.nodeType === 1 ? anchor : anchor.parentNode)) return;
     const text = sel.toString().trim();
     setBodyHighlight(text.length >= 4 ? text : '');
   };
   document.addEventListener('selectionchange', onSel);
   return () => document.removeEventListener('selectionchange', onSel);
 }, []);

 // Look up the parent module title so we can pass it into the builder as
 // additional context (helps the AI stay on-topic).
 const parentModuleTitle = (() => {
 if (!lessonForm.module_id) return '';
 for (const course of courses || []) {
 const details = courseDetails?.[course.id];
 const m = details?.modules?.find((mm) => mm.id === lessonForm.module_id);
 if (m) return m.title;
 }
 return '';
 })();

 const handlePlanInsert = (_markdown, plan) => {
 const current = lessonForm.description || '';

 // Fold generated quiz items into a real Quiz content section so the
 // admin doesn't have to retype them into the Quiz Builder. Each
 // question maps to the existing shape: {question, type, options[],
 // correct_answer, explanation}. answer_index → the option string
 // (Quiz Builder stores the correct answer as a string, not an index).
 const quizItems = Array.isArray(plan?.quiz) ? plan.quiz : [];
 const nextSections = [...(lessonForm.content_sections || [])];
 let addedQuizSection = false;
 if (quizItems.length > 0) {
 const questions = quizItems.map((q) => {
 const options = Array.isArray(q.options) ? q.options.slice(0, 4) : ['', '', '', ''];
 // Pad to 4 options so the Quiz Builder UI is fully populated.
 while (options.length < 4) options.push('');
 const idx = Number.isInteger(q.answer_index) ? q.answer_index : 0;
 const safeIdx = Math.max(0, Math.min(options.length - 1, idx));
 return {
 question: q.question || '',
 type: 'multiple_choice',
 options,
 correct_answer: options[safeIdx] || '',
 explanation: q.explanation || '',
 };
 }).filter((q) => q.question);
 if (questions.length > 0) {
 nextSections.push({
 id: `temp_${Date.now()}_quiz`,
 title: `AI-generated quiz — ${plan?.title || 'Lesson'}`,
 type: 'quiz',
 order: nextSections.length,
 video_url: '',
 text_content: '',
 markdown_content: '',
 pdf_url: '',
 presentation_url: '',
 quiz_data: {
 passing_score: 70,
 questions,
 },
 checklist_data: { items: [], require_all: true },
 poll_data: { question: '', options: [], allow_multiple: false },
 });
 addedQuizSection = true;
 }
 }

 // Regenerate the description markdown WITHOUT the quiz block if we
 // wired the quiz into its own section — otherwise the admin would see
 // both a plain-text quiz in the description AND the real quiz below.
 const md = planToMarkdown(plan, { includeQuiz: !addedQuizSection });
 const asHtml = md
 .split('\n')
 .map((line) => (line.trim()
 ? `<div>${line.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>`
 : '<div><br/></div>'))
 .join('');

 setLessonForm({
 ...lessonForm,
 description: `${current}${current ? '<div><br/></div>' : ''}${asHtml}`,
 content_sections: nextSections,
 });
 };

 return (
 <Card className="mb-6">
 <CardHeader>
 <div className="flex items-center justify-between gap-3 flex-wrap">
 <CardTitle>{editingLesson ? 'Edit Lesson' : 'Add Lesson to Module'}</CardTitle>
 <div className="flex items-center gap-2">
 {editingLesson?.id && (
 <Button
 variant="outline"
 size="sm"
 onClick={() => setShowWorkpad(true)}
 data-testid="open-ai-workpad-btn"
 title="Freeform AI workpad — generate text or Nano Banana images and copy into any section"
 >
 <Wand2 className="w-4 h-4 mr-2 text-primary" />
 AI Workpad
 </Button>
 )}
 <Button
 variant="outline"
 size="sm"
 onClick={() => setShowPlanBuilder(true)}
 data-testid="lesson-plan-builder-open-btn"
 title="Build lesson content with AI grounded in your Knowledge Base + Standards of Education"
 >
 <Wand2 className="w-4 h-4 mr-2 text-amber-500" />
 Build with AI
 </Button>
 </div>
 </div>
 </CardHeader>
 <CardContent>
 <AIWorkpad
 open={showWorkpad}
 onClose={() => setShowWorkpad(false)}
 lessonId={editingLesson?.id}
 />
 <LessonPlanBuilderModal
 open={showPlanBuilder}
 onOpenChange={setShowPlanBuilder}
 onInsert={handlePlanInsert}
 initialTopic={lessonForm.title || ''}
 initialTier="pro"
 moduleTitle={parentModuleTitle}
 />
 {/* Element-scoped AI content generator — only shown when editing
 an existing lesson (needs a lesson_id). New lessons must be
 saved first, then reopened, before AI content can be generated. */}
 {editingLesson?.id && (
 <div className="mb-4 flex items-center gap-2">
 <Button
 type="button"
 variant="outline"
 size="sm"
 onClick={() => setShowPreview(true)}
 data-testid="lesson-preview-btn"
 className="whitespace-nowrap"
 >
 <BookOpen className="w-4 h-4 mr-2" />
 Preview as student
 </Button>
 <span className="text-xs text-slate-500 dark:text-neutral-400">
 Opens this lesson exactly as learners will see it (body, quiz, images, citations) in an inline modal.
 </span>
 </div>
 )}
 <LessonPreviewModal
   open={showPreview}
   onOpenChange={setShowPreview}
   lessonId={editingLesson?.id}
   lessonTitle={editingLesson?.title}
 />
 {editingLesson?.id && (
 <div className="mb-4">
 <LessonAIBuilder
 lessonId={editingLesson.id}
 lessonTitle={editingLesson.title}
 lesson={editingLesson}
 onGenerated={async () => {
 // After the AI job finishes, refetch the fresh lesson row
 // so newly-appended sections (quiz / image / etc.) show up
 // in the section list without the admin having to close
 // and re-open the form. Silent on failure — the sections
 // are still persisted server-side.
 try {
 const { data } = await axios.get(`${API}/lms/lessons/${editingLesson.id}`);
 if (data) {
 setLessonForm((prev) => ({
 ...prev,
 description:      data.description || prev.description,
 content_sections: data.content_sections || prev.content_sections || [],
 }));
 toast.success(`Loaded ${(data.content_sections || []).length} sections into editor`);
 }
 } catch (_) { /* silent */ }
 }}
 />
 </div>
 )}
 {/* Breadcrumb showing parent course and module (compact) */}
 {lessonForm.module_id && (
 <div className="mb-4 space-y-2">
 {/* Find the module and its course */}
 {(() => {
 let parentModule = null;
 let parentCourse = null;
 
 for (const course of courses) {
 const details = courseDetails[course.id];
 if (details?.modules) {
 const module = details.modules.find(m => m.id === lessonForm.module_id);
 if (module) {
 parentModule = module;
 parentCourse = course;
 break;
 }
 }
 }
 
 return (
 <>
 {parentCourse && (
 <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
 <div className="flex items-center gap-2 text-sm">
 <BookOpen className="w-3 h-3 text-blue-600" />
 <span className="text-slate-600 dark:text-neutral-400">Course:</span>
 <span className="font-medium text-slate-900 dark:text-white">
 {parentCourse.title}
 </span>
 </div>
 </div>
 )}
 {parentModule && (
 <div className="p-2 bg-primary/5 dark:bg-navy/20 rounded-lg border border-primary/30 dark:border-primary/30">
 <div className="flex items-center gap-2 text-sm">
 <List className="w-3 h-3 text-primary" />
 <span className="text-slate-600 dark:text-neutral-400">Module:</span>
 <span className="font-medium text-slate-900 dark:text-white">
 {parentModule.title}
 </span>
 </div>
 </div>
 )}
 </>
 );
 })()}
 </div>
 )}
 <div className="space-y-4">
 {/* Citation tools — mount above the title so admins see them first
     and can either insert markers manually or run AI suggest against
     a saved lesson. `getLiveContent` streams the current unsaved
     editor state (description + every content section's HTML/text)
     so Suggest works before the lesson is even saved. */}
 <CitationTools
   lessonId={editingLesson?.id}
   getLiveContent={() => {
     const chunks = [];
     if (lessonForm?.description) chunks.push(lessonForm.description);
     if (lessonForm?.written_content) chunks.push(lessonForm.written_content);
     if (lessonForm?.markdown_content) chunks.push(lessonForm.markdown_content);
     for (const s of (lessonForm?.content_sections || [])) {
       for (const k of ['content', 'html', 'markdown', 'text']) {
         if (s?.[k]) chunks.push(String(s[k]));
       }
     }
     return chunks.join('\n\n');
   }}
 />

 <div>
 <Label>Lesson Title</Label>
 <Input
 value={lessonForm.title}
 onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
 placeholder="Lesson 1: Introduction"
 />
 </div>
 <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
 <div className="space-y-3" ref={editorWrapperRef} data-testid="lesson-body-editor-wrapper">
 <Label>Description (optional)</Label>
 <RichTextEditor
 value={lessonForm.description}
 onChange={(value) => setLessonForm({ ...lessonForm, description: value })}
 placeholder="Brief overview of this lesson..."
 aiContext={{ lessonId: editingLesson?.id }}
 />
 <CitedSourcesChipStrip
 description={lessonForm.description}
 onChangeDescription={(next) => setLessonForm({ ...lessonForm, description: next })}
 />
 </div>
 <KBCitationSidebar
 description={lessonForm.description}
 pinnedIds={lessonForm.pinned_kb_ids || []}
 canRegenerate={!!editingLesson?.id}
 highlight={bodyHighlight}
 onInsertToBody={(nextHtml) => setLessonForm({ ...lessonForm, description: nextHtml })}
 onTogglePin={(id) => {
 const cur = new Set(lessonForm.pinned_kb_ids || []);
 if (cur.has(id)) cur.delete(id); else cur.add(id);
 setLessonForm({ ...lessonForm, pinned_kb_ids: Array.from(cur) });
 }}
 onRegenerate={async () => {
 if (!editingLesson?.id) { toast.info('Save the lesson first'); return; }
 try {
 const { data: start } = await axios.post(`${API}/admin/ai/lesson-content/start`, {
 lesson_id: editingLesson.id,
 pinned_kb_ids: lessonForm.pinned_kb_ids || [],
 });
 toast.success('Regeneration queued — pinned sources will be cited');
 const jobId = start?.job_id;
 const started = Date.now();
 let consecErrs = 0;
 while (jobId && Date.now() - started <= 300000) {
 await new Promise((r) => setTimeout(r, 2500));
 let job;
 try {
 const { data } = await axios.get(`${API}/admin/ai/lesson-content/job/${jobId}`);
 job = data;
 consecErrs = 0;
 } catch (_pollErr) {
 consecErrs += 1;
 if (consecErrs >= 8) { toast.error('Could not reach the server — please retry.'); return; }
 continue;
 }
 if (job?.status === 'done' || job?.status === 'partial') {
 toast.success('Regeneration complete — reload the lesson to see updates');
 return;
 }
 if (job?.status === 'failed') { toast.error(job.error || 'Regeneration failed'); return; }
 }
 } catch (e) {
 toast.error(e?.response?.data?.detail || 'Regeneration failed');
 }
 }}
 />
 </div>

 {/* Multi-Content Section Builder */}
 <div className="border-2 border-blue-200 dark:border-blue-800 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20">
 <h4 className="font-semibold mb-3 flex items-center gap-2 text-blue-900 dark:text-blue-100">
 🎬 Multi-Content Lesson Builder
 </h4>
 <p className="text-sm text-slate-600 dark:text-neutral-400 mb-4">
 Add multiple content sections (video + text + PDFs + presentations) to create rich lessons
 </p>

 {/* Existing Content Sections */}
 {lessonForm.content_sections.length > 0 && (
 <div className="mb-4 space-y-2">
 <Label className="text-sm font-semibold">Current Sections ({lessonForm.content_sections.length})</Label>
 {lessonForm.content_sections.map((section, index) => (
 <div 
 key={section.id} 
 className={`flex items-center gap-2 p-3 rounded border ${
 editingSectionId === section.id 
 ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-500' 
 : 'bg-white dark:bg-slate-800'
 }`}
 >
 <div className="flex gap-1">
 <Button
 size="sm"
 variant="ghost"
 onClick={() => onMoveContentSection(index, 'up')}
 disabled={index === 0}
 className="h-7 w-7 p-0"
 >
 ↑
 </Button>
 <Button
 size="sm"
 variant="ghost"
 onClick={() => onMoveContentSection(index, 'down')}
 disabled={index === lessonForm.content_sections.length - 1}
 className="h-7 w-7 p-0"
 >
 ↓
 </Button>
 </div>
 <div className="flex-1">
 <span className="font-medium">{section.title}</span>
 <span className="ml-2 text-xs px-2 py-1 bg-slate-200 dark:bg-slate-700 rounded">
 {section.type}
 {section.type === 'quiz' && section.quiz_data?.questions?.length > 0 && (
 <span className="ml-1 font-semibold text-blue-600 dark:text-blue-400">
 ({section.quiz_data.questions.length} questions)
 </span>
 )}
 </span>
 {editingSectionId === section.id && (
 <span className="ml-2 text-xs px-2 py-1 bg-blue-600 text-white rounded">
 ✏️ Editing
 </span>
 )}
 {/* Image thumbnail preview so admins SEE what the AI diagram
 generator produced. Larger (h-40 ≈ 160 px) so the admin can
 actually read the diagram before opening Edit. Falls back to
 the compact h-12 preview when the section is squeezed into
 a narrow layout. */}
 {section.type === 'image' && section.image_url && (
 <div className="mt-2 w-full">
 <img
 src={section.image_url}
 alt={section.image_alt || section.title || 'diagram'}
 className="max-h-40 w-auto rounded-md border border-slate-300 dark:border-slate-600 shadow-sm"
 loading="lazy"
 data-testid={`admin-section-thumb-${section.id || ''}`}
 />
 {section.image_alt && (
 <p className="text-[11px] text-slate-500 dark:text-neutral-400 italic mt-1 max-w-2xl">
 {section.image_alt}
 </p>
 )}
 </div>
 )}
 </div>
 <Button
 size="sm"
 variant="outline"
 onClick={() => onEditContentSection(section)}
 disabled={editingSectionId !== null && editingSectionId !== section.id}
 >
 <Edit className="w-4 h-4" />
 </Button>
 {section.type === 'quiz' && (
 <QuizRegenerateButton
 section={section}
 lessonTitle={lessonForm.title || ''}
 onReplace={(questions) => {
 // Replace this section's questions in the parent form. We
 // avoid a full section-array rewrite so unrelated fields
 // (checklist_data, poll_data, ordering) stay intact.
 setLessonForm({
 ...lessonForm,
 content_sections: lessonForm.content_sections.map((s) =>
 s.id === section.id
 ? { ...s, quiz_data: { ...(s.quiz_data || {}), questions } }
 : s
 ),
 });
 }}
 />
 )}
 {section.type === 'image' && (
 <ImageRegenerateButton
 lessonId={editingLesson?.id}
 section={section}
 onReplace={(updated) => {
 // In-place swap so ordering and title stay put.
 setLessonForm({
 ...lessonForm,
 content_sections: lessonForm.content_sections.map((s) =>
 s.id === updated.id ? { ...s, ...updated } : s
 ),
 });
 }}
 />
 )}
 <Button
 size="sm"
 variant="ghost"
 onClick={() => onRemoveContentSection(section.id)}
 className="text-red-600"
 >
 <Trash2 className="w-4 h-4" />
 </Button>
 </div>
 ))}
 </div>
 )}

 {/* Add New Section Form */}
 <div className="border-t pt-4 space-y-3">
 <div className="flex items-center justify-between">
 <Label className="text-sm font-semibold">
 {editingSectionId ? '✏️ Edit Section' : '➕ Add New Section'}
 </Label>
 {editingSectionId && (
 <Button
 size="sm"
 variant="ghost"
 onClick={onCancelEditSection}
 className="text-xs"
 >
 Cancel Edit
 </Button>
 )}
 </div>
 
 <div>
 <Label>Section Title</Label>
 <Input
 value={contentSectionForm.title}
 onChange={(e) => setContentSectionForm({ ...contentSectionForm, title: e.target.value })}
 placeholder="e.g., Introduction Video, Course Materials, Practice Exercise"
 />
 </div>

 <div>
 <Label>Section Type</Label>
 <select
 value={contentSectionForm.type}
 onChange={(e) => setContentSectionForm({ ...contentSectionForm, type: e.target.value })}
 className="w-full p-2 border rounded-lg"
 >
 <option value="markdown">📄 Textbook (Markdown)</option>
 <option value="text">📝 Rich Text/HTML</option>
 <option value="video">📹 Video</option>
 <option value="pdf">📑 PDF Document</option>
 <option value="presentation">📊 Presentation</option>
 <option value="quiz">❓ Quiz / Assessment</option>
 <option value="checklist">✅ Checklist</option>
 <option value="poll">📊 Poll</option>
 </select>
 </div>

 {/* Content Type Specific Fields - Enhanced Editors */}
 {contentSectionForm.type === 'video' && (
 <VideoInput
 value={contentSectionForm.video_url}
 onChange={(value) => setContentSectionForm({ ...contentSectionForm, video_url: value })}
 />
 )}

 {contentSectionForm.type === 'text' && (
 <div>
 <Label className="mb-2 block">Rich Text Content</Label>
 <RichTextEditor
 value={contentSectionForm.text_content}
 onChange={(value) => setContentSectionForm({ ...contentSectionForm, text_content: value })}
 placeholder="Start writing your content with formatting..."
 aiContext={{ lessonId: editingLesson?.id }}
 />
 </div>
 )}

 {contentSectionForm.type === 'markdown' && (
 <div>
 <div className="flex items-center justify-between mb-2">
 <Label>Markdown Content</Label>
 <MarkdownAIToolbar
 value={contentSectionForm.markdown_content}
 onChange={(v) => setContentSectionForm({ ...contentSectionForm, markdown_content: v })}
 aiContext={{ lessonId: editingLesson?.id }}
 sectionType="markdown"
 />
 </div>
 <textarea
 value={contentSectionForm.markdown_content}
 onChange={(e) => setContentSectionForm({ ...contentSectionForm, markdown_content: e.target.value })}
 placeholder="# Heading\n\nYour markdown content..."
 className="w-full p-2 border rounded-lg min-h-[120px] font-mono text-sm"
 />
 <p className="text-xs text-slate-500 mt-1">
 💡 Use markdown syntax for formatting
 </p>
 </div>
 )}

 {contentSectionForm.type === 'pdf' && (
 <DocumentInput
 value={contentSectionForm.pdf_url}
 onChange={(value) => setContentSectionForm({ ...contentSectionForm, pdf_url: value })}
 type="pdf"
 />
 )}

 {contentSectionForm.type === 'presentation' && (
 <DocumentInput
 value={contentSectionForm.presentation_url}
 onChange={(value) => setContentSectionForm({ ...contentSectionForm, presentation_url: value })}
 type="presentation"
 />
 )}

 {contentSectionForm.type === 'quiz' && (
 <div className="space-y-4 border-2 border-blue-200 dark:border-blue-800 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20">
 <h4 className="font-semibold text-blue-900 dark:text-blue-100">❓ Quiz Builder</h4>
 
 {/* Quiz Settings */}
 <div className="grid grid-cols-2 gap-3">
 <div>
 <Label>Passing Score (%)</Label>
 <Input
 type="number"
 value={contentSectionForm.quiz_data?.passing_score || 70}
 onChange={(e) => setContentSectionForm({
 ...contentSectionForm,
 quiz_data: {
 ...contentSectionForm.quiz_data,
 passing_score: parseInt(e.target.value)
 }
 })}
 min="0"
 max="100"
 />
 </div>
 <div className="flex items-center gap-2 pt-6">
 <input
 type="checkbox"
 checked={contentSectionForm.quiz_data?.show_feedback !== false}
 onChange={(e) => setContentSectionForm({
 ...contentSectionForm,
 quiz_data: {
 ...contentSectionForm.quiz_data,
 show_feedback: e.target.checked
 }
 })}
 className="w-4 h-4"
 />
 <Label>Show immediate feedback</Label>
 </div>
 </div>

 {/* Existing Questions */}
 {contentSectionForm.quiz_data?.questions?.length > 0 && (
 <div className="space-y-2">
 <Label className="font-semibold">Questions ({contentSectionForm.quiz_data.questions.length})</Label>
 {contentSectionForm.quiz_data.questions.map((q, index) => (
 <div 
 key={index} 
 className={`p-3 rounded border flex items-start gap-2 ${
 editingQuestionIndex === index 
 ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-500' 
 : 'bg-white dark:bg-slate-800'
 }`}
 >
 <span className="font-bold text-blue-600">{index + 1}.</span>
 <div className="flex-1">
 <p className="font-medium">{q.question}</p>
 <span className="text-xs px-2 py-1 bg-slate-200 dark:bg-slate-700 rounded">
 {q.type === 'multiple_choice' ? 'Multiple Choice' : 
 q.type === 'true_false' ? 'True/False' : 'Short Answer'}
 </span>
 {editingQuestionIndex === index && (
 <span className="ml-2 text-xs px-2 py-1 bg-blue-600 text-white rounded">
 ✏️ Editing
 </span>
 )}
 </div>
 <Button
 type="button"
 size="sm"
 variant="outline"
 onClick={() => onEditQuizQuestion(index)}
 disabled={editingQuestionIndex !== null && editingQuestionIndex !== index}
 >
 <Edit className="w-4 h-4" />
 </Button>
 <Button
 type="button"
 size="sm"
 variant="ghost"
 onClick={() => onRemoveQuizQuestion(index)}
 className="text-red-600"
 >
 <Trash2 className="w-4 h-4" />
 </Button>
 </div>
 ))}
 </div>
 )}

 {/* Add Question Form */}
 <div className="border-t pt-4 space-y-3">
 <div className="flex items-center justify-between">
 <Label className="font-semibold">
 {editingQuestionIndex !== null ? '✏️ Edit Question' : '➕ Add Question'}
 </Label>
 {editingQuestionIndex !== null && (
 <Button
 type="button"
 size="sm"
 variant="ghost"
 onClick={onCancelEditQuizQuestion}
 className="text-xs"
 >
 Cancel Edit
 </Button>
 )}
 </div>
 
 <div>
 <Label>Question Type</Label>
 <select
 value={quizQuestion.type}
 onChange={(e) => setQuizQuestion({ ...quizQuestion, type: e.target.value })}
 className="w-full p-2 border rounded-lg"
 >
 <option value="multiple_choice">Multiple Choice</option>
 <option value="true_false">True/False</option>
 <option value="short_answer">Short Answer</option>
 </select>
 </div>

 <div>
 <Label>Question Text</Label>
 <Input
 value={quizQuestion.question}
 onChange={(e) => setQuizQuestion({ ...quizQuestion, question: e.target.value })}
 placeholder="What is the main benefit of freeze drying?"
 />
 </div>

 {quizQuestion.type === 'multiple_choice' && (
 <>
 <div>
 <Label>Answer Options (one per line)</Label>
 <textarea
 value={quizQuestion.options?.join('\n') || ''}
 onChange={(e) => setQuizQuestion({
 ...quizQuestion,
 options: e.target.value.split('\n')
 })}
 placeholder="Option A\nOption B\nOption C\nOption D"
 className="w-full p-2 border rounded-lg min-h-[100px]"
 />
 </div>
 <div>
 <Label>Correct Answer (exact match)</Label>
 <Input
 value={quizQuestion.correct_answer}
 onChange={(e) => setQuizQuestion({ ...quizQuestion, correct_answer: e.target.value })}
 placeholder="Option A"
 />
 </div>
 </>
 )}

 {quizQuestion.type === 'true_false' && (
 <div>
 <Label>Correct Answer</Label>
 <select
 value={quizQuestion.correct_answer}
 onChange={(e) => setQuizQuestion({ ...quizQuestion, correct_answer: e.target.value })}
 className="w-full p-2 border rounded-lg"
 >
 <option value="">Select...</option>
 <option value="True">True</option>
 <option value="False">False</option>
 </select>
 </div>
 )}

 {quizQuestion.type === 'short_answer' && (
 <div>
 <Label>Expected Answer</Label>
 <Input
 value={quizQuestion.correct_answer}
 onChange={(e) => setQuizQuestion({ ...quizQuestion, correct_answer: e.target.value })}
 placeholder="Key terms or phrase"
 />
 <p className="text-xs text-slate-500 mt-1">
 Note: Short answers require manual grading
 </p>
 </div>
 )}

 <div>
 <Label>Explanation (optional)</Label>
 <textarea
 value={quizQuestion.explanation}
 onChange={(e) => setQuizQuestion({ ...quizQuestion, explanation: e.target.value })}
 placeholder="Why this answer is correct..."
 className="w-full p-2 border rounded-lg min-h-[60px]"
 />
 </div>

 <Button
 type="button"
 onClick={() => {
 if (editingQuestionIndex !== null) {
 onUpdateQuizQuestion();
 } else {
 onAddQuizQuestion();
 }
 }}
 variant="outline"
 className={`w-full ${
 editingQuestionIndex !== null 
 ? 'bg-green-600 hover:bg-green-700' 
 : 'bg-blue-600 hover:bg-blue-700'
 } text-white`}
 >
 {editingQuestionIndex !== null ? (
 <>
 <Save className="w-4 h-4 mr-2" />
 Update Question
 </>
 ) : (
 <>
 <Plus className="w-4 h-4 mr-2" />
 Add Question
 </>
 )}
 </Button>
 </div>
 </div>
 )}

 {contentSectionForm.type === 'checklist' && (
 <div className="space-y-3 border-2 border-emerald-200 dark:border-emerald-800 rounded-lg p-4 bg-emerald-50 dark:bg-emerald-900/20">
 <h4 className="font-semibold text-emerald-900 dark:text-emerald-100">✅ Checklist Builder</h4>
 <p className="text-xs text-emerald-800 dark:text-emerald-200">
 Enter one checklist item per line. Learners tick off items as they progress.
 </p>
 <textarea
 value={(contentSectionForm.checklist_data?.items || []).map(i => i.label).join('\n')}
 onChange={(e) => {
 const lines = e.target.value.split('\n').map(l => l.trim()).filter(Boolean);
 const items = lines.map((label, idx) => ({
 id: (contentSectionForm.checklist_data?.items?.[idx]?.id) || `chk_${Date.now()}_${idx}`,
 label,
 }));
 setContentSectionForm({
 ...contentSectionForm,
 checklist_data: {
 items,
 require_all: contentSectionForm.checklist_data?.require_all ?? true,
 },
 });
 }}
 placeholder="Sanitize the tray&#10;Weigh product to 3.4 kg&#10;Set primary dry temp to -30°C"
 className="w-full p-2 border rounded-lg min-h-[120px] font-mono text-sm"
 />
 <label className="flex items-center gap-2 text-sm text-emerald-900 dark:text-emerald-100">
 <input
 type="checkbox"
 checked={contentSectionForm.checklist_data?.require_all ?? true}
 onChange={(e) => setContentSectionForm({
 ...contentSectionForm,
 checklist_data: {
 ...(contentSectionForm.checklist_data || { items: [] }),
 require_all: e.target.checked,
 },
 })}
 />
 Require all items to mark section done
 </label>
 </div>
 )}

 {contentSectionForm.type === 'poll' && (
 <div className="space-y-3 border-2 border-primary/40 dark:border-primary/60 rounded-lg p-4 bg-primary/10 dark:bg-primary/10">
 <h4 className="font-semibold text-foreground">📊 Poll Builder</h4>
 <div>
 <Label>Poll Question</Label>
 <Input
 value={contentSectionForm.poll_data?.question || ''}
 onChange={(e) => setContentSectionForm({
 ...contentSectionForm,
 poll_data: {
 ...(contentSectionForm.poll_data || { options: [], allow_multiple: false }),
 question: e.target.value,
 },
 })}
 placeholder="What size machine are you running?"
 />
 </div>
 <div>
 <Label>Options (one per line)</Label>
 <textarea
 value={(contentSectionForm.poll_data?.options || []).map(o => o.label).join('\n')}
 onChange={(e) => {
 const lines = e.target.value.split('\n').map(l => l.trim()).filter(Boolean);
 const options = lines.map((label, idx) => ({
 id: (contentSectionForm.poll_data?.options?.[idx]?.id) || `opt_${Date.now()}_${idx}`,
 label,
 }));
 setContentSectionForm({
 ...contentSectionForm,
 poll_data: {
 question: contentSectionForm.poll_data?.question || '',
 allow_multiple: contentSectionForm.poll_data?.allow_multiple || false,
 options,
 },
 });
 }}
 placeholder="Small (under 5 trays)&#10;Medium (6-12 trays)&#10;Large (13+ trays)"
 className="w-full p-2 border rounded-lg min-h-[100px] font-mono text-sm"
 />
 </div>
 <label className="flex items-center gap-2 text-sm text-foreground">
 <input
 type="checkbox"
 checked={contentSectionForm.poll_data?.allow_multiple ?? false}
 onChange={(e) => setContentSectionForm({
 ...contentSectionForm,
 poll_data: {
 ...(contentSectionForm.poll_data || { question: '', options: [] }),
 allow_multiple: e.target.checked,
 },
 })}
 />
 Allow multi-select
 </label>
 </div>
 )}

 <Button
 onClick={onAddContentSection}
 variant="outline"
 className={`w-full ${
 editingSectionId 
 ? 'bg-green-600 hover:bg-green-700' 
 : 'bg-blue-600 hover:bg-blue-700'
 } text-white`}
 >
 {editingSectionId ? (
 <>
 <Save className="w-4 h-4 mr-2" />
 Update Section
 </>
 ) : (
 <>
 <Plus className="w-4 h-4 mr-2" />
 Add Section to Lesson
 </>
 )}
 </Button>
 </div>
 </div>

 {/* Legacy Single Content (Hidden if using multi-content) */}
 {lessonForm.content_sections.length === 0 && (
 <>
 <div className="border-t pt-4">
 <p className="text-sm text-slate-600 dark:text-neutral-400 mb-3">
 Or use legacy single-content mode:
 </p>
 </div>
 <div>
 <Label>Content Type</Label>
 <select
 value={lessonForm.content_type}
 onChange={(e) => setLessonForm({ ...lessonForm, content_type: e.target.value })}
 className="w-full p-2 border rounded-lg"
 >
 <option value="markdown">Textbook (Markdown)</option>
 <option value="text">Rich Text</option>
 <option value="video">Video</option>
 <option value="pdf">PDF</option>
 </select>
 </div>
 {lessonForm.content_type === 'video' && (
 <div>
 <Label>YouTube URL</Label>
 <Input
 value={lessonForm.video_url}
 onChange={(e) => setLessonForm({ ...lessonForm, video_url: e.target.value })}
 placeholder="https://www.youtube.com/watch?v=..."
 />
 </div>
 )}
 {lessonForm.content_type === 'text' && (
 <div>
 <Label>Written Content (HTML)</Label>
 <textarea
 value={lessonForm.written_content}
 onChange={(e) => setLessonForm({ ...lessonForm, written_content: e.target.value })}
 placeholder="<p>Lesson content...</p>"
 className="w-full p-2 border rounded-lg min-h-[150px] font-mono text-sm"
 />
 </div>
 )}
 {lessonForm.content_type === 'pdf' && (
 <div>
 <Label>PDF URL</Label>
 <Input
 value={lessonForm.pdf_url}
 onChange={(e) => setLessonForm({ ...lessonForm, pdf_url: e.target.value })}
 placeholder="https://..."
 />
 </div>
 )}
 {lessonForm.content_type === 'markdown' && (
 <div>
 <Label>Markdown Content</Label>
 <textarea
 value={lessonForm.markdown_content}
 onChange={(e) => setLessonForm({ ...lessonForm, markdown_content: e.target.value })}
 placeholder="# Heading\n\nContent..."
 className="w-full p-2 border rounded-lg min-h-[150px] font-mono text-sm"
 />
 </div>
 )}
 </>
 )}

 <div>
 <Label>Duration (minutes)</Label>
 <Input
 type="number"
 value={lessonForm.duration_minutes}
 onChange={(e) => setLessonForm({ ...lessonForm, duration_minutes: e.target.value })}
 placeholder="15"
 />
 </div>
 <div>
 <Label>Order</Label>
 <Input
 type="number"
 value={lessonForm.order}
 onChange={(e) => setLessonForm({ ...lessonForm, order: parseInt(e.target.value) })}
 />
 </div>

 {/* Enhanced Content Tools - Collapsible */}
 <div className="border-t pt-4 mt-4">
 <button
 type="button"
 onClick={() => setExpandedSections({ ...expandedSections, enhancedTools: !expandedSections.enhancedTools })}
 className="w-full flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
 >
 <h4 className="font-semibold flex items-center gap-2">
 🛠️ Enhanced Content Tools (Optional)
 </h4>
 {expandedSections.enhancedTools ? (
 <ChevronUp className="w-5 h-5" />
 ) : (
 <ChevronDown className="w-5 h-5" />
 )}
 </button>

 {expandedSections.enhancedTools && (
 <div className="mt-4 space-y-4">
 {/* Download Manager */}
 <DownloadManager
 downloads={lessonForm.support_downloads}
 onChange={(downloads) => setLessonForm({ ...lessonForm, support_downloads: downloads })}
 />

 {/* Image Manager */}
 <ImageManager
 images={lessonForm.support_photos?.map((url, idx) => ({ 
 id: `img-${idx}`, 
 url, 
 caption: '', 
 alt: '' 
 }))}
 onChange={(images) => setLessonForm({ 
 ...lessonForm, 
 support_photos: images.map(img => img.url) 
 })}
 />
 </div>
 )}
 </div>

 {/* Support Materials Section - Collapsible */}
 <div className="border-t pt-4 mt-4">
 <button
 type="button"
 onClick={() => setExpandedSections({ ...expandedSections, supportMaterials: !expandedSections.supportMaterials })}
 className="w-full flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
 >
 <h4 className="font-semibold flex items-center gap-2">
 📎 Additional Support Materials (Optional)
 </h4>
 {expandedSections.supportMaterials ? (
 <ChevronUp className="w-5 h-5" />
 ) : (
 <ChevronDown className="w-5 h-5" />
 )}
 </button>

 {expandedSections.supportMaterials && (
 <div className="mt-4 space-y-3">
 {/* Support Photos */}
 <div>
 <Label>Photos (one URL per line)</Label>
 <textarea
 value={lessonForm.support_photos?.join('\n') || ''}
 onChange={(e) => setLessonForm({ 
 ...lessonForm, 
 support_photos: e.target.value.split('\n').filter(url => url.trim()) 
 })}
 placeholder="https://example.com/photo1.jpg&#10;https://example.com/photo2.jpg"
 className="w-full p-2 border rounded-lg min-h-[80px] font-mono text-sm"
 />
 <p className="text-xs text-slate-500 mt-1">Images will be displayed in a gallery</p>
 </div>

 {/* Support Videos */}
 <div>
 <Label>Additional Videos (one YouTube URL per line)</Label>
 <textarea
 value={lessonForm.support_videos?.join('\n') || ''}
 onChange={(e) => setLessonForm({ 
 ...lessonForm, 
 support_videos: e.target.value.split('\n').filter(url => url.trim()) 
 })}
 placeholder="https://www.youtube.com/watch?v=...&#10;https://www.youtube.com/watch?v=..."
 className="w-full p-2 border rounded-lg min-h-[80px] font-mono text-sm"
 />
 <p className="text-xs text-slate-500 mt-1">Supplementary video resources</p>
 </div>

 {/* Support Links */}
 <div>
 <Label>Related Links (Title | URL format, one per line)</Label>
 <textarea
 value={lessonForm.support_links?.map(link => `${link.title} | ${link.url}`).join('\n') || ''}
 onChange={(e) => {
 const links = e.target.value.split('\n')
 .filter(line => line.trim())
 .map(line => {
 const parts = line.split('|').map(p => p.trim());
 return { title: parts[0] || '', url: parts[1] || '' };
 });
 setLessonForm({ ...lessonForm, support_links: links });
 }}
 placeholder="Official Documentation | https://docs.example.com&#10;Tutorial Video | https://youtube.com/..."
 className="w-full p-2 border rounded-lg min-h-[80px] font-mono text-sm"
 />
 <p className="text-xs text-slate-500 mt-1">Format: Title | URL (separated by pipe)</p>
 </div>

 {/* Support Downloads */}
 <div>
 <Label>Downloadable Files (Title | URL format, one per line)</Label>
 <textarea
 value={lessonForm.support_downloads?.map(dl => `${dl.title} | ${dl.url}`).join('\n') || ''}
 onChange={(e) => {
 const downloads = e.target.value.split('\n')
 .filter(line => line.trim())
 .map(line => {
 const parts = line.split('|').map(p => p.trim());
 return { title: parts[0] || '', url: parts[1] || '' };
 });
 setLessonForm({ ...lessonForm, support_downloads: downloads });
 }}
 placeholder="Worksheet PDF | https://example.com/worksheet.pdf&#10;Reference Guide | https://example.com/guide.pdf"
 className="w-full p-2 border rounded-lg min-h-[80px] font-mono text-sm"
 />
 <p className="text-xs text-slate-500 mt-1">PDFs, worksheets, templates, etc.</p>
 </div>
 </div>
 )}
 </div>

 {/* Advertising Section - Collapsible */}
 <div className="border-t pt-4 mt-4">
 <button
 type="button"
 onClick={() => setExpandedSections({ ...expandedSections, advertising: !expandedSections.advertising })}
 className="w-full flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
 >
 <h4 className="font-semibold flex items-center gap-2">
 📢 Advertisement Settings (Optional)
 </h4>
 {expandedSections.advertising ? (
 <ChevronUp className="w-5 h-5" />
 ) : (
 <ChevronDown className="w-5 h-5" />
 )}
 </button>

 {expandedSections.advertising && (
 <div className="mt-4 space-y-3">
 <div className="flex items-center gap-2">
 <input
 type="checkbox"
 checked={lessonForm.ad_enabled || false}
 onChange={(e) => setLessonForm({ ...lessonForm, ad_enabled: e.target.checked })}
 className="w-4 h-4"
 />
 <Label>Enable Advertisement for this Lesson</Label>
 </div>
 
 {lessonForm.ad_enabled && (
 <>
 <div>
 <Label>Ad Position</Label>
 <select
 value={lessonForm.ad_position || 'top'}
 onChange={(e) => setLessonForm({ ...lessonForm, ad_position: e.target.value })}
 className="w-full p-2 border rounded-lg"
 >
 <option value="top">Top of Lesson</option>
 <option value="middle">Middle of Lesson</option>
 <option value="bottom">Bottom of Lesson</option>
 </select>
 </div>
 <div>
 <Label>Ad Title</Label>
 <Input
 value={lessonForm.ad_title || ''}
 onChange={(e) => setLessonForm({ ...lessonForm, ad_title: e.target.value })}
 placeholder="Exclusive Tools Available!"
 />
 </div>
 <div>
 <Label>Ad Content</Label>
 <Input
 value={lessonForm.ad_content || ''}
 onChange={(e) => setLessonForm({ ...lessonForm, ad_content: e.target.value })}
 placeholder="Access premium calculators and resources..."
 />
 </div>
 <div>
 <Label>Ad Image URL</Label>
 <Input
 value={lessonForm.ad_image_url || ''}
 onChange={(e) => setLessonForm({ ...lessonForm, ad_image_url: e.target.value })}
 placeholder="https://..."
 />
 </div>
 <div>
 <Label>Ad Link URL</Label>
 <Input
 value={lessonForm.ad_link_url || ''}
 onChange={(e) => setLessonForm({ ...lessonForm, ad_link_url: e.target.value })}
 placeholder="https://..."
 />
 </div>
 <div>
 <Label>Ad Link Text</Label>
 <Input
 value={lessonForm.ad_link_text || ''}
 onChange={(e) => setLessonForm({ ...lessonForm, ad_link_text: e.target.value })}
 placeholder="Upgrade Now"
 />
 </div>
 </>
 )}
 </div>
 )}
 </div>
 </div>
 <div className="flex gap-3 mt-4">
 <Button onClick={onSave} className="bg-primary hover:bg-primary/90">
 <Save className="w-4 h-4 mr-2" />
 {editingLesson ? 'Update Lesson' : 'Create Lesson'}
 </Button>
 <Button onClick={onCancel} variant="outline">
 <X className="w-4 h-4 mr-2" />
 Cancel
 </Button>
 </div>
 </CardContent>
 </Card>
 );
};

export default LessonForm;
